import React from 'react'
import { CveShieldsDefaultsGridView } from './cve-shields-defaults-grid-view'
import {
  GenericFilterer,
  GenericSorter,
  SortOrder
} from '../../legacy-design-system/legacy-grid'
import { CveShield, ProtectRule } from '../protect-rules/shared'
import { useCveShieldsSummary } from './use-cve-shields-summary'
import { ViewerLoader } from '../viewer'

interface CveShieldsDefaultsManagerProps {
  readonly organizationUuid: string
}

export function CveShieldsDefaultsManager({
  organizationUuid
}: CveShieldsDefaultsManagerProps) {
  const { data, isLoading, error } = useCveShieldsSummary(organizationUuid)

  if (error) {
    throw new Error(error.message)
  }

  const rules = data ? data : []
  const normalizedCveShields = rules.map(normalizeProtectRuleToCveShield)

  const filterByName = (item: CveShield, value: string) =>
    item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1

  const filterByUuid = (item: CveShield, value: string) =>
    item.uuid.toLowerCase().indexOf(value.toLowerCase()) !== -1

  /* TODO - TS-9606 - replace ViewerLoader usage with ReactQuery */
  return (
    <ViewerLoader>
      {({ viewer, loading: viewerLoading }) => {
        if (viewerLoading || !viewer) {
          return false
        }

        const {
          currentOrganization: { role }
        } = viewer

        return (
          <GenericFilterer<CveShield>
            data={normalizedCveShields}
            filterFns={[filterByName, filterByUuid]}
          >
            {({ filtered, onSearch }) => {
              return (
                <GenericSorter<CveShield>
                  data={filtered}
                  defaultSortConfig={{
                    field: 'name',
                    order: SortOrder.ASC,
                    getValue: ({ name }) => name
                  }}
                >
                  {({ sorted, onSort, activeSortConfig }) => {
                    return (
                      <CveShieldsDefaultsGridView
                        organizationUuid={organizationUuid}
                        organizationRole={role}
                        loading={isLoading}
                        rowData={sorted}
                        onSort={onSort}
                        activeSortConfigs={
                          !!activeSortConfig ? [activeSortConfig] : []
                        }
                        onSearch={onSearch}
                      />
                    )
                  }}
                </GenericSorter>
              )
            }}
          </GenericFilterer>
        )
      }}
    </ViewerLoader>
  )
}

function normalizeProtectRuleToCveShield({
  ruleId,
  name,
  uuid,
  description,
  development,
  qa,
  production,
  languages,
  cves
}: ProtectRule): CveShield {
  return {
    id: ruleId.toString(),
    name,
    uuid,
    description,
    development,
    qa,
    production,
    languages,
    cves
  }
}
